import { ComboboxItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import { FormStoreProps } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import { useMemo, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useParams } from 'react-router';
import { useOrganizationData } from '../organizations/organization.hooks';
import {
  Group,
  GroupFormValues,
  Question,
  QuestionFormValues,
  useGetConditions,
  useGetQipChoices,
  useGetQipQuestions,
  useGroupById,
  useQuestionById,
} from './api/qip';

export function useHydrateQuestion(
  updateStore: (fn: (state: FormStoreProps<QuestionFormValues>) => void) => void,
) {
  const { questionId } = useParams();

  useQuestionById(questionId || '', {
    enabled: !!questionId,
    onSuccess: (data: Question) => updateStore((x) => Object.assign(x, data)),
  });

  return { questionId };
}

export function useChoiceData() {
  const { data } = useGetQipChoices();

  return useMemo(() => {
    const mapped = (data ?? []).map((c) => ({
      label: c.name,
      value: c.id,
      questionId: c.qipMetricQuestionId,
      index: c.index,
    }));
    mapped.sort((a, b) => a.index - b.index);
    return mapped;
  }, [data]);
}

export function useQuestionData() {
  const { data } = useGetQipQuestions();

  return useMemo(() => {
    const mapped = (data?.filter((q) => q.status === 'Active') || []).map((q) => ({
      label: q.name,
      value: q.id,
      type: q.type,
    }));
    return mapped;
  }, [data]);
}

export function useConditionData(groupId: string) {
  const { data: conditions } = useGetConditions(groupId, {
    enabled: !!groupId,
  });
  const { data: choices } = useGetQipChoices();
  const { data: questions } = useGetQipQuestions();

  return useMemo(() => {
    const mapped = (conditions ?? []).map((c) => ({
      id: c.id,
      questionId: c.questionId,
      choiceToTriggerId: {
        label: choices?.find((choice) => choice.id === c.choiceToTriggerId)?.name,
        value: c.choiceToTriggerId,
      },
      questionToShowId: {
        label: questions?.find((q) => q.id === c.questionToShowId)?.name,
        value: c.questionToShowId,
      },
    }));
    return mapped;
  }, [conditions, choices, questions, groupId]);
}

function mapSelectedValues(values: string[], source: ComboboxItem[] = []) {
  if (!values || !source?.length) return null;
  return source.filter((x) => values.some((y) => y === x.value));
}

export function useHydrateGroup(
  updateStore: (fn: (state: FormStoreProps<GroupFormValues>) => void) => void,
) {
  const { groupId } = useParams();
  const orgData = useOrganizationData();
  const questionData = useQuestionData();
  const choiceData = useChoiceData();
  const conditionData = useConditionData(groupId ?? '');
  const [group, setGroup] = useState<Group>();

  useGroupById(groupId || '', {
    enabled: !!(groupId && orgData),
    onSuccess: (data: Group) => {
      setGroup(data);
      updateStore((x) =>
        Object.assign(x, {
          ...data,
          organizationIds: mapSelectedValues(data.organizationIds, orgData),
          questionIds: data.questionIds,
        }),
      );
    },
  });

  return { groupId, group, orgData, questionData, choiceData, conditionData };
}
